import {
  AppointmentStatus,
  ConditionLogicId,
  IAppointmentScopeData,
  IBrand,
  IConditionLogicImplementation,
  IDynamicForm,
  isAppointment,
  isPatient,
  isStaffer,
  IStaffer,
  ITag,
  ITreatmentCategory,
  ITreatmentConfiguration,
} from '@principle-theorem/principle-core/interfaces';
import {
  asyncForEach,
  DocumentReference,
  Firestore,
  getEnumValues,
  INamedDocument,
  isObject,
  isSameRef,
  multiFilter,
  snapshot,
  splitCamel,
  titlecase,
  toNamedDocument,
  TypeGuard,
  undeletedFilter,
  WithRef,
} from '@principle-theorem/shared';
import { first, isString, sortBy } from 'lodash';
import { Brand } from '../models/brand';
import { TreatmentConfiguration } from '../models/clinical-charting/treatment/treatment-configuration';
import { TreatmentStep } from '../models/clinical-charting/treatment/treatment-step';
import { Patient } from '../models/patient/patient';
import { Staffer } from '../models/staffer/staffer';
import { DynamicForm } from './dynamic-form';

export const isAppointmentScopeData =
  TypeGuard.interface<IAppointmentScopeData>({
    patient: isPatient,
    medicalHistoryLink: isString,
    referrer: TypeGuard.noGuard(),
    appointment: isAppointment,
    practitioner: isStaffer,
    confirmLink: isString,
    practice: isObject,
  });

export interface IAppointmentHasTagConditionConfig {
  tag: INamedDocument<ITag>;
}

export class AppointmentHasTagCondition
  implements
    IConditionLogicImplementation<
      IAppointmentScopeData,
      IAppointmentHasTagConditionConfig,
      INamedDocument<ITag>
    >
{
  conditionId = ConditionLogicId.AppointmentHasTag;
  description = 'Appointment must have the selected tag';
  isValidContext = isAppointmentScopeData;

  async isTrue(
    config: IAppointmentHasTagConditionConfig,
    context: IAppointmentScopeData
  ): Promise<boolean> {
    const tags = await asyncForEach(context.appointment.tags, (tag) =>
      Firestore.getDoc(tag.ref)
    );
    return tags
      .filter(undeletedFilter)
      .some((appointmentTag) => isSameRef(appointmentTag.ref, config.tag.ref));
  }

  async getForm(
    brand: WithRef<IBrand>,
    initialValue?: IAppointmentHasTagConditionConfig
  ): Promise<IDynamicForm<INamedDocument<ITag>>> {
    const tags = await Firestore.getDocs(Brand.appointmentTagCol(brand));
    const options = tags.filter(undeletedFilter).map((tag) => ({
      label: tag.name,
      value: toNamedDocument(tag),
    }));

    const tag = DynamicForm.select({
      label: 'Tag',
      options: sortBy(options, 'label'),
      initialValue: initialValue?.tag ?? first(options)?.value,
      compareWith: isSameRef,
    });
    return { tag };
  }

  async getSummary(
    config: IAppointmentHasTagConditionConfig
  ): Promise<string | undefined> {
    return Promise.resolve(`Appointment has tag "${config.tag.name}"`);
  }
}

export interface IAppointmentIsStatusConditionConfig {
  status: AppointmentStatus;
}

export class AppointmentIsStatusCondition
  implements
    IConditionLogicImplementation<
      IAppointmentScopeData,
      IAppointmentIsStatusConditionConfig,
      AppointmentStatus
    >
{
  conditionId = ConditionLogicId.AppointmentIsStatus;
  description = 'Appointment must be the selected status';
  isValidContext = isAppointmentScopeData;

  isTrue(
    config: IAppointmentIsStatusConditionConfig,
    context: IAppointmentScopeData
  ): boolean {
    return context.appointment.status === config.status;
  }

  async getForm(
    _brand: WithRef<IBrand>,
    initialValue?: IAppointmentIsStatusConditionConfig
  ): Promise<IDynamicForm<AppointmentStatus>> {
    const options = getEnumValues(AppointmentStatus).map((status) => ({
      label: status,
      value: status,
    }));
    const status = DynamicForm.select({
      label: 'Status',
      options,
      initialValue: initialValue?.status ?? AppointmentStatus.Confirmed,
    });
    return Promise.resolve({ status });
  }

  async getSummary(
    config: IAppointmentIsStatusConditionConfig
  ): Promise<string | undefined> {
    const statusName = titlecase(splitCamel(config.status));
    return Promise.resolve(`Appointment is "${statusName}"`);
  }
}

export interface IAppointmentConfirmedConditionConfig {
  isConfirmed: boolean;
}

export class AppointmentConfirmedCondition
  implements
    IConditionLogicImplementation<
      IAppointmentScopeData,
      IAppointmentConfirmedConditionConfig,
      boolean
    >
{
  conditionId = ConditionLogicId.AppointmentConfirmed;
  description = 'Appointment must be Confirmed';
  isValidContext = isAppointmentScopeData;

  isTrue(
    config: IAppointmentConfirmedConditionConfig,
    context: IAppointmentScopeData
  ): boolean {
    const isConfirmed =
      context.appointment.status === AppointmentStatus.Confirmed;
    return isConfirmed === config.isConfirmed;
  }

  async getForm(
    _brand: WithRef<IBrand>,
    initialValue?: IAppointmentConfirmedConditionConfig
  ): Promise<IDynamicForm<boolean>> {
    const isConfirmed = DynamicForm.select<boolean>({
      label: 'Is Confirmed?',
      options: [
        { label: 'Confirmed', value: true },
        { label: 'Not Confirmed', value: false },
      ],
      initialValue: initialValue?.isConfirmed ?? true,
    });
    return Promise.resolve({ isConfirmed });
  }

  async getSummary(
    config: IAppointmentConfirmedConditionConfig
  ): Promise<string | undefined> {
    return Promise.resolve(
      `Appointment is ${config.isConfirmed ? '' : 'not '}Confirmed`
    );
  }
}

interface IIsFirstAppointment {
  isFirst: boolean;
}

export class IsFirstAppointmentCondition
  implements
    IConditionLogicImplementation<
      IAppointmentScopeData,
      IIsFirstAppointment,
      boolean
    >
{
  conditionId = ConditionLogicId.IsFirstAppointment;
  description = `Must be the Patient's first Appointment`;
  isValidContext = isAppointmentScopeData;

  async isTrue(
    config: IIsFirstAppointment,
    context: IAppointmentScopeData
  ): Promise<boolean> {
    const isFirst = await snapshot(
      Patient.isNewPatient$(context.patient, context.appointment.ref)
    );
    return isFirst === config.isFirst;
  }

  async getForm(
    _brand: WithRef<IBrand>,
    initialValue?: IIsFirstAppointment
  ): Promise<IDynamicForm<boolean>> {
    const isFirst = DynamicForm.select<boolean>({
      label: 'Is First Appointment?',
      options: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
      initialValue: initialValue?.isFirst ?? true,
    });
    return Promise.resolve({ isFirst });
  }

  async getSummary(config: IIsFirstAppointment): Promise<string | undefined> {
    return Promise.resolve(
      `Appointment is ${
        config.isFirst ? '' : 'not '
      }the Patient's first Appointment`
    );
  }
}

export interface IIncludesTreatmentConfiguration {
  treatmentConfigurations: DocumentReference<ITreatmentConfiguration>[];
}

export class AppointmentIncludesTreatmentCondition
  implements
    IConditionLogicImplementation<
      IAppointmentScopeData,
      IIncludesTreatmentConfiguration,
      DocumentReference<ITreatmentConfiguration>
    >
{
  conditionId = ConditionLogicId.AppointmentIncludesTreatment;
  description = 'Appointment must have one of these Treatments';
  isValidContext = isAppointmentScopeData;

  async isTrue(
    config: IIncludesTreatmentConfiguration,
    context: IAppointmentScopeData
  ): Promise<boolean> {
    const treatmentStep = await Firestore.getDoc(
      context.appointment.treatmentPlan.treatmentStep.ref
    );
    const appointmentTreatmentConfigurations = await snapshot(
      TreatmentStep.getTreatmentConfigurations$(treatmentStep)
    );

    if (
      config.treatmentConfigurations.length &&
      !appointmentTreatmentConfigurations.length
    ) {
      return false;
    }

    return appointmentTreatmentConfigurations.some(
      (appointmentTreatmentConfiguration) =>
        config.treatmentConfigurations.some((treatmentConfiguration) =>
          isSameRef(treatmentConfiguration, appointmentTreatmentConfiguration)
        )
    );
  }

  async getForm(
    brand: WithRef<IBrand>,
    initialData?: IIncludesTreatmentConfiguration
  ): Promise<IDynamicForm<DocumentReference<ITreatmentConfiguration>>> {
    const options = await Firestore.getDocs(TreatmentConfiguration.col(brand));
    const treatmentConfigurations = DynamicForm.select<
      DocumentReference<ITreatmentConfiguration>
    >({
      label: 'Has one of these Treatments?',
      options: sortBy(options, (option) => option.name).map(
        (configuration) => ({
          label: configuration.name,
          value: configuration.ref,
        })
      ),
      initialValue: initialData?.treatmentConfigurations ?? [],
      compareWith: isSameRef,
      isMultiple: true,
    });
    return { treatmentConfigurations };
  }

  async getSummary(
    config: IIncludesTreatmentConfiguration
  ): Promise<string | undefined> {
    const treatmentNames = await asyncForEach(
      config.treatmentConfigurations,
      async (treatmentRef) => {
        const treatment = await Firestore.getDoc(treatmentRef);
        return `"${treatment.name}"`;
      }
    );
    const treatments = treatmentNames.join(' OR ');
    return `Appointment includes ${treatments}`;
  }
}

export interface IExcludesTreatmentConfiguration {
  excludeTreatmentConfigurations: DocumentReference<ITreatmentConfiguration>[];
}

export class AppointmentExcludesTreatmentCondition
  implements
    IConditionLogicImplementation<
      IAppointmentScopeData,
      IExcludesTreatmentConfiguration,
      DocumentReference<ITreatmentConfiguration>
    >
{
  conditionId = ConditionLogicId.AppointmentExcludesTreatment;
  description = 'Appointment must not be one of these Treatments';
  isValidContext = isAppointmentScopeData;

  async isTrue(
    config: IExcludesTreatmentConfiguration,
    context: IAppointmentScopeData
  ): Promise<boolean> {
    const treatmentStep = await Firestore.getDoc(
      context.appointment.treatmentPlan.treatmentStep.ref
    );
    const appointmentTreatmentConfigurations = await snapshot(
      TreatmentStep.getTreatmentConfigurations$(treatmentStep)
    );

    return appointmentTreatmentConfigurations.every(
      (appointmentTreatmentConfiguration) =>
        config.excludeTreatmentConfigurations.every(
          (treatmentConfiguration) =>
            !isSameRef(
              treatmentConfiguration,
              appointmentTreatmentConfiguration
            )
        )
    );
  }

  async getForm(
    brand: WithRef<IBrand>,
    initialData?: IExcludesTreatmentConfiguration
  ): Promise<IDynamicForm<DocumentReference<ITreatmentConfiguration>>> {
    const options = await Firestore.getDocs(TreatmentConfiguration.col(brand));
    const excludeTreatmentConfigurations = DynamicForm.select<
      DocumentReference<ITreatmentConfiguration>
    >({
      label: `Doesn't have one of these Treatments?`,
      options: sortBy(options, (option) => option.name).map(
        (configuration) => ({
          label: configuration.name,
          value: configuration.ref,
        })
      ),
      compareWith: isSameRef,
      initialValue: initialData?.excludeTreatmentConfigurations ?? [],
      isMultiple: true,
    });
    return { excludeTreatmentConfigurations };
  }

  async getSummary(
    config: IExcludesTreatmentConfiguration
  ): Promise<string | undefined> {
    const treatmentNames = await asyncForEach(
      config.excludeTreatmentConfigurations,
      async (treatmentRef) => {
        const treatment = await Firestore.getDoc(treatmentRef);
        return `"${treatment.name}"`;
      }
    );
    const treatments = treatmentNames.join(' OR ');
    return `Appointment does not include ${treatments}`;
  }
}

interface IIncludesTreatmentCategory {
  treatmentCategories: DocumentReference<ITreatmentCategory>[];
}

export class AppointmentIncludesTreatmentCategoryCondition
  implements
    IConditionLogicImplementation<
      IAppointmentScopeData,
      IIncludesTreatmentCategory,
      DocumentReference<ITreatmentCategory>
    >
{
  conditionId = ConditionLogicId.AppointmentIsTreatmentCategory;
  description = 'Appointment must be one of these Treatment Categories';
  isValidContext = isAppointmentScopeData;

  isTrue(
    config: IIncludesTreatmentCategory,
    context: IAppointmentScopeData
  ): boolean {
    const appointmentPrimaryCategory = TreatmentStep.defaultDisplayRef(
      context.appointment.treatmentPlan.treatmentStep.display
    );

    return config.treatmentCategories.some((treatmentCategory) =>
      isSameRef(treatmentCategory, appointmentPrimaryCategory)
    );
  }

  async getForm(
    brand: WithRef<IBrand>,
    initialData?: IIncludesTreatmentCategory
  ): Promise<IDynamicForm<DocumentReference<ITreatmentCategory>>> {
    const options = await Firestore.getDocs(Brand.treatmentCategoryCol(brand));
    const treatmentCategories = DynamicForm.select<
      DocumentReference<ITreatmentCategory>
    >({
      label: 'Is one of these Treatment Categories?',
      options: sortBy(
        options.filter(undeletedFilter),
        (option) => option.name
      ).map((category) => ({
        label: category.name,
        value: category.ref,
      })),
      compareWith: isSameRef,
      initialValue: initialData?.treatmentCategories ?? [],
      isMultiple: true,
    });
    return { treatmentCategories };
  }

  async getSummary(
    config: IIncludesTreatmentCategory
  ): Promise<string | undefined> {
    const categoryNames = await asyncForEach(
      config.treatmentCategories,
      async (categoryRef) => {
        const category = await Firestore.getDoc(categoryRef);
        return `"${category.name}"`;
      }
    );
    const categories = categoryNames.join(' OR ');
    return `Appointment category is ${categories}`;
  }
}

interface IExcludesTreatmentCategory {
  excludeTreatmentCategories: DocumentReference<ITreatmentCategory>[];
}

export class AppointmentExcludesTreatmentCategoryCondition
  implements
    IConditionLogicImplementation<
      IAppointmentScopeData,
      IExcludesTreatmentCategory,
      DocumentReference<ITreatmentCategory>
    >
{
  conditionId = ConditionLogicId.AppointmentIsNotTreatmentCategory;
  description = 'Appointment must not be one of these Treatment Categories';
  isValidContext = isAppointmentScopeData;

  isTrue(
    config: IExcludesTreatmentCategory,
    context: IAppointmentScopeData
  ): boolean {
    const appointmentPrimaryCategory = TreatmentStep.defaultDisplayRef(
      context.appointment.treatmentPlan.treatmentStep.display
    );

    return config.excludeTreatmentCategories.every(
      (treatmentCategory) =>
        !isSameRef(treatmentCategory, appointmentPrimaryCategory)
    );
  }

  async getForm(
    brand: WithRef<IBrand>,
    initialData?: IExcludesTreatmentCategory
  ): Promise<IDynamicForm<DocumentReference<ITreatmentCategory>>> {
    const options = await Firestore.getDocs(Brand.treatmentCategoryCol(brand));
    const excludeTreatmentCategories = DynamicForm.select<
      DocumentReference<ITreatmentCategory>
    >({
      label: 'Is not one of these Treatment Categories?',
      options: sortBy(
        options.filter(undeletedFilter),
        (option) => option.name
      ).map((category) => ({
        label: category.name,
        value: category.ref,
      })),
      initialValue: initialData?.excludeTreatmentCategories ?? [],
      compareWith: isSameRef,
      isMultiple: true,
    });
    return { excludeTreatmentCategories };
  }

  async getSummary(
    config: IExcludesTreatmentCategory
  ): Promise<string | undefined> {
    const categoryNames = await asyncForEach(
      config.excludeTreatmentCategories,
      async (categoryRef) => {
        const category = await Firestore.getDoc(categoryRef);
        return `"${category.name}"`;
      }
    );
    const categories = categoryNames.join(' OR ');
    return `Appointment category is not ${categories}`;
  }
}

interface IIncludesPractitioner {
  practitioners: DocumentReference<IStaffer>[];
}

export class AppointmentIncludesPractitionerCondition
  implements
    IConditionLogicImplementation<
      IAppointmentScopeData,
      IIncludesPractitioner,
      DocumentReference<IStaffer>
    >
{
  conditionId = ConditionLogicId.AppointmentIncludesPractitioner;
  description = 'Appointment must be with one of these Practitioners';
  isValidContext = isAppointmentScopeData;

  isTrue(
    config: IIncludesPractitioner,
    context: IAppointmentScopeData
  ): boolean {
    const appointmentPractitioner = context.appointment.practitioner;

    return config.practitioners.some((practitioner) =>
      isSameRef(practitioner, appointmentPractitioner)
    );
  }

  async getForm(
    brand: WithRef<IBrand>,
    initialData?: IIncludesPractitioner
  ): Promise<IDynamicForm<DocumentReference<IStaffer>>> {
    const options = await snapshot(
      Staffer.practitionersByBrand$(brand).pipe(
        multiFilter((practitioner) => practitioner.deleted === false)
      )
    );
    const practitioners = DynamicForm.select<DocumentReference<IStaffer>>({
      label: 'Is one of these Practitioners?',
      options: sortBy(options, (option) => option.user.name).map(
        (practitioner) => ({
          label: practitioner.user.name,
          value: practitioner.ref,
        })
      ),
      initialValue: initialData?.practitioners ?? [],
      isMultiple: true,
    });
    return { practitioners };
  }

  async getSummary(config: IIncludesPractitioner): Promise<string | undefined> {
    const practitionerNames = await asyncForEach(
      config.practitioners,
      async (practitionerRef) => {
        const practitioner = await Firestore.getDoc(practitionerRef);
        return `"${practitioner.user.name}"`;
      }
    );
    const practitioners = practitionerNames.join(' OR ');
    return `Appointment is with ${practitioners}`;
  }
}

interface IExcludesPractitioner {
  excludePractitioners: DocumentReference<IStaffer>[];
}

export class AppointmentExcludesPractitionerCondition
  implements
    IConditionLogicImplementation<
      IAppointmentScopeData,
      IExcludesPractitioner,
      DocumentReference<IStaffer>
    >
{
  conditionId = ConditionLogicId.AppointmentExcludesPractitioner;
  description = 'Appointment must not be with any of these Practitioners';
  isValidContext = isAppointmentScopeData;

  isTrue(
    config: IExcludesPractitioner,
    context: IAppointmentScopeData
  ): boolean {
    const appointmentPractitioner = context.appointment.practitioner;

    return config.excludePractitioners.every(
      (practitioner) => !isSameRef(practitioner, appointmentPractitioner)
    );
  }

  async getForm(
    brand: WithRef<IBrand>,
    initialData?: IExcludesPractitioner
  ): Promise<IDynamicForm<DocumentReference<IStaffer>>> {
    const options = await snapshot(
      Staffer.practitionersByBrand$(brand).pipe(
        multiFilter((practitioner) => practitioner.deleted === false)
      )
    );
    const excludePractitioners = DynamicForm.select<
      DocumentReference<IStaffer>
    >({
      label: 'Is not any of these Practitioners',
      options: sortBy(options, (option) => option.user.name).map(
        (practitioner) => ({
          label: practitioner.user.name,
          value: practitioner.ref,
        })
      ),
      initialValue: initialData?.excludePractitioners ?? [],
      isMultiple: true,
    });
    return { excludePractitioners };
  }

  async getSummary(config: IExcludesPractitioner): Promise<string | undefined> {
    const practitionerNames = await asyncForEach(
      config.excludePractitioners,
      async (practitionerRef) => {
        const practitioner = await Firestore.getDoc(practitionerRef);
        return `"${practitioner.user.name}"`;
      }
    );
    const practitioners = practitionerNames.join(' OR ');
    return `Appointment is not with ${practitioners}`;
  }
}
